import styles from 'components/cases/common/Interview/Interview.module.scss'
import TitleFlex from 'components/cases/common/TitleFlex'
import { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import InterviewContents from 'components/cases/common/Interview/InterviewContents'
import { ContentData } from 'components/cases/common/Interview/fragments/type'
gsap.registerPlugin(ScrollTrigger)

const sectionTitle: { title: string; sub_title: string } = {
  title: 'Interview',
  sub_title: 'インタビュー',
}

const contentData: ContentData[][] = [
  [
    {
      type: 'sectionTitle',
      text: '時代に合わせた変化への挑戦、求めたのは「想いを形にする伴走者」',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview01.webp',
      alt: '左からアイスリーデザインの後藤、武本、セゾンテクノロジーの真田さん、宇佐美さん、鹿山さん',
      text: '左からアイスリーデザインの後藤、武本、セゾンテクノロジーの真田さん、宇佐美さん、鹿山さん',
    },
    {
      type: 'textBox',
      texts: ['―― プロジェクト前にどのような課題をお持ちであったかを教えてください。'],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '宇佐美さん',
      texts: [
        <>
          <p>
            HULFTは業界内で寡占状態にあり、現状、お客様からの不満は特に聞こえてきていないものの、長年使われてきた製品であるため、このままでは<span className={styles.textbold}>「新しい世代には受け入れられないかもしれない」という危機感</span>がありました。
          </p>
          <p>
            そこで、今後に向けて調査の実施を検討しましたが、スモールスタートを目指して自部門だけで取り組むことに決めました。とはいえ、私たちのチームにはそのための専門的なノウハウを持ったプロがいません。また、<span className={styles.textbold}>内部だけで進めると「ちょっとした改良止まり」で終わってしまう</span>恐れもありました。部長である私が主導すると、メンバーも「上司の意向に沿わなければ」と萎縮してしまうことも否めず、発想の幅に限界が生じかねません。そのため、外部の力を借りることにしました。
          </p>
        </>,
      ],
    },
    {
      type: 'textBox',
      texts: ['―― ベンダーを選定するうえで大切にしたポイント、基準はなんでしょうか。',],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '真田さん',
      texts: [
        <>
          <p>
            まずは「実績や経験」を重視しました。<span className={styles.textbold}>特にUXの分野では、経験値の高さが成果に直結する</span>部分が大きいと考えたためです。また、「<span className={styles.textbold}>リサーチからデザイン、コンサルティングまでワンストップ</span>で対応できるかどうか」もポイントでした。そして、コミュニケーション面も非常に大切にしました。アイスリーデザインさんは、実際に手を動かす方とも直接お話ができ、こちらの意図をしっかり汲み取ってくれると感じたのが、最終的な決め手になりました。
          </p>
          <p>
            今回のプロジェクトは「思いを形にする」ことがテーマでしたので、「<span className={styles.textbold}>思いを一番に尊重してくれる伴走者</span>」であることを重視しました。アイスリーデザインさんは、打ち合わせの段階からしっかり話を聞いてくれ、一方的な提案ではなく「一緒に走っていく」という姿勢が伝わってきたことが大きかったです。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: '「平成」から「令和」へ、デザインの方向性を明確化',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview02.webp',
      alt: '「平成」から「令和」へ、デザインの方向性を明確化',
    },
    {
      type: 'textBox',
      texts: [
        '―― 先方の課題をどのように解決していきましたか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '武本',
      texts: [
        <>
          <p>
            まずは、HULFTが市場でどのように評価されているのかを明確にするため、プロダクト目線で市場調査を行いました。続いてエキスパートレビューを実施。ヒューリスティクス10原則やUX5段階モデルに基づいて具体的な改善点を指摘しました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview03.webp',
      alt: 'エキスパートレビュー実施レポート',
    },
    {
      type: 'greyBox',
      texts: [
        <>
          <p>
            ユーザーインタビューでは、セゾンテクノロジーさんの新卒社員30名を対象にNPSアンケートを配布、さらに5名の方には直接インタビューを行いました。その結果を基に、UIデザインのプロトタイプを作成し、最低限の改善を施した案、大胆に構造を見直した案、そして現状の製品を加えた3案を用意して若い世代の反応を検証しました。プロトタイプの評価結果から、現状製品よりも改善案が支持され、特に大胆に見直したデザインが高評価を得たことが確認できました。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview04.webp',
      alt: '改善したUI/UXデザイン',
    },
    {
      type: 'textBox',
      texts: [
        '―― 本プロジェクトで特に難易度が高かったポイントを教えてください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '武本',
      texts: [
        <>
          <p>
            HULFTはすでに市場シェア1位<sup>※</sup>の製品です。そのため「使いづらいから変える」という単純な改善ではなく、<span className={styles.textbold}>「将来を見据えてどう進化させるか」</span>を考える難しさがありました。そのためにも将来のユーザー層である若い世代の声を集める必要がありましたが、製品を使ったことがない層にただ見せても「使いづらい」で終わってしまうリスクもありました。そこで、日頃から最先端のプロダクトに触れている、セゾンテクノロジーさんの20代新卒社員にインタビューを実施し、ユーザー視点を収集しました。結果として、「平成っぽい」などといった<span className={styles.textbold}>リアルな声をたくさん集められた</span>のは大きな収穫だったと感じています。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '宇佐美さん',
      texts: [
        <>
          <p>
            いただいたインタビュー結果は、<span className={styles.textbold}>上層部や他部門への説明にも非常に役立ちました</span>。また、「HULFT Technology Days 2024」という大規模イベントでも、「Z世代にも受け入れられ、今後20〜30年使い続けてもらいたい」というビジョンを伝える際に活用しました。そのなかでも特に、「平成っぽい」というキーワードは会場にお越しいただいたユーザー企業の皆さまにも強く響いていました。<span className={styles.textbold}>「この製品は令和を狙っているんだ」というメッセージが伝わった</span>と感じています。
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview05.webp',
      alt: '「HULFT Technology Days 2024」に登壇された宇佐美さん',
      text: '「HULFT Technology Days 2024」に登壇された宇佐美さん',
    },
    {
      type: 'textBox',
      texts: [
        '―― プロジェクトが進む中で、アイスリーデザインにはどのような印象を持ちましたか？',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '真田さん',
      texts: [
        <>
          <p>
            製品に対するキャッチアップの早さには本当に驚かされました。私たちは自社製品に慣れすぎていて、その複雑さや分かりにくさに気づきにくい部分があったのですが、アイスリーデザインさんは実際に製品を操作し、細部まで丁寧に掘り下げて改善点を指摘してくださいました。<span className={styles.textbold}>「ここまでしっかり見てくれるんだ」と感じ、とても安心感がありました。</span>
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '後藤',
      texts: [
        <>
          <p>
            ありがとうございます。私たちも製品を実際に操作する中で、多くの学びがありました。UI設計は、やはり実際に触れてみないと分からない部分が多く、特に細かな動きや使い勝手は、静止した画像からはなかなかイメージしづらいものです。ですので、実際に手を動かしながら、慎重に確認を進めました。エキスパートレビューでも、改善点の洗い出しのために、すべての画面をキャプチャしています。
          </p>
        </>,
      ],
    },
  ],
  [
    {
      type: 'sectionTitle',
      text: 'ユーザーインタビューの結果が、今後の社内改善への指標に',
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview06.webp',
      alt: 'ユーザーインタビューの結果が、今後の社内改善への指標に',
    },
    {
      type: 'textBox',
      texts: [
        '―― 振り返って、一緒に仕事をして良かったと感じた点があればお聞かせください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '鹿山さん',
      texts: [
        <>
          <p>
            社内ではどうしても制約が多く、発想が固定化してしまうことがありましたが、アイスリーデザインさんと一緒に仕事をすることで違うアプローチを体験できました。<span className={styles.textbold}>製品背景の整理、4P分析、ユーザーリサーチからUIデザインへのプロセスは「こういうことをやりたかった！」と思えるもの</span>でした。多角的な視点や新アイデアをいただき、自身のデザイン手法やアプローチの幅も広がりました。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '真田さん',
      texts: [
        <>
          <p>
            アイスリーデザインさんに入っていただいたことで、「自分の考えは間違っていなかった」と感じられ、安心感につながりました。エキスパートレビューでは、私たちが「当たり前だ」と思っていたことを丁寧に言語化していただき、<span className={styles.textbold}>他部署にも伝わりやすくなった</span>のが印象的でした。
          </p>
        </>,
      ],
    },
    {
      type: 'greyBox',
      name: '宇佐美さん',
      texts: [
        <>
          <p>
            エキスパートレビューに基づいて大幅にブラッシュアップされたプロトタイプが私たちの描いていたイメージと近かったので、私たちの進むべき方向は間違っていないと確認できたのは嬉しかったですね。この確認が取れたことで、資料として上層部にも出せる状態になり、<span className={styles.textbold}>これからのスタートを切る上で大きな自信になりました。</span>
          </p>
        </>,
      ],
    },
    {
      type: 'imageWithCaption',
      src: '/images/img/cases/hulft/interview07.webp',
      alt: '1Member 2Client 3Monitor',
    },
    {
      type: 'textBox',
      texts: [
        '―― 今後の目標やマイルストーンがあれば教えてください。',
      ],
      wide: false,
      bold: true,
    },
    {
      type: 'greyBox',
      name: '宇佐美さん',
      texts: [
        <>
          <p>
            当社は<span className={styles.textbold}>プロダクトのライフサイクルの改善</span>に取り組んでいます。具体的には、HULFT10の新製品が出たとしても、ユーザーの声をキャッチアップすることから改善までの横断的な取り組みを進めている段階です。なので、まずは私たち自身ができることとして、製品に興味を持ってくださった方々とお話しする機会を大切にしたいと考えています。実際の声を伺うことで、仮説に基づいて進めるだけでなく、リアルなデータや意見に基づいて改善を進めていけるはずです。同時に、<span className={styles.textbold}>社内のプロダクトマネージャーとUXデザイナーの育成</span>も進めています。この「育った人材」と「整ったユーザー理解」の2軸が交わるとき、次の一手を打つタイミングが来ると思っています。
          </p>
        </>,
      ],
    },
    {
      type: 'imageLink',
      src: '/images/img/cases/hulft/interview08.webp',
      srcsp: '/images/img/cases/hulft/interview09.webp',
      alt: 'ファイル転送ミドルウェア HULFT10 詳細はこちら',
      link: 'https://www.hulft.com/',
    },
  ],
]

const tweenVars: gsap.TimelineVars = {
  y: 50,
  duration: 1,
  opacity: 0,
  ease: Power4.easeOut,
}

const Interview: React.FC = () => {
  const timeline = gsap.timeline()
  const wrapper = useRef<HTMLElement>()
  const image = useRef<HTMLImageElement>()
  const text_container = useRef<HTMLDivElement>()

  useEffect(() => {
    timeline.from(image.current, tweenVars, 0.2)
    timeline.from(text_container.current, tweenVars, 0.4)

    ScrollTrigger.create({
      animation: timeline,
      trigger: wrapper.current,
      start: 'top-=100 90%',
      toggleActions: 'play none none play',
    })
  }, [])

  return (
    <section className={styles.wrapper} ref={wrapper}>
      <div className={styles.content}>
        <TitleFlex sectionTitle={sectionTitle} />
        <div className={styles.article_container} ref={text_container}>
          <InterviewContents contentData={contentData} />
        </div>
      </div>
    </section>
  )
}

export default Interview
